import { selectMktxDealers } from './marketAxessDealers';
import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TuneIcon from "@mui/icons-material/Tune";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import { IntegerInput, FloatInput } from "./NumericInputs";
import { fieldOptions } from "./optionsHelper";

import {
  compactInputStyle,
  dealerInputStyle,
  DEALER_PREVIEW_RIGHT_PX,
} from "./listRfqStyles";
import { ROW_STATUS } from "../../Store/listRfqStore";
import {
  MKTX_STRATEGY_TYPES,
  mktxStrategyUsesDealers,
  ORDER_SIDES,
  PROTOCOL_TYPES,
  TRUMID_PARTIAL_EXECUTION,
  TRUMID_PRICE_TYPES,
  TRUMID_STIPULATION_TYPES,
} from "../../Constants/AppConstants";
import { tokens, withAlpha } from "../../Common/designTokens";
import { localToUtc, utcToLocal } from "../../Utils/timeConversions";

/**
 * Every per-line-item field, one column each, all on the row.
 *
 * These are exactly the tags the Trumid RFQ spec nests inside the NoRelatedSym <146>
 * repeating group (pp.47-51, marked with a leading arrow), so each one is genuinely per
 * bond and the trader may set them differently on every line item. None of them is hidden
 * or collapsed: the grid is wider than the 800px modal and scrolls sideways instead.
 *
 * `tag` records which FIX tag each column carries. It is NOT rendered anywhere - it was the
 * column header's tooltip until the headings were made plain - and is kept purely as the
 * written link from a column to its place in the spec, for whoever reads this file next.
 *
 * `price` is the one column with no tag: createListRfqDto sends 44 and 218 as null, exactly
 * as the single-bond createRfqDto does, so it is the order's own price on order_mst and
 * never reaches the venue.
 *
 * 64 SettlDate is NOT a column. It IS per line item on the wire - buildListRfqPayload puts
 * it on every bond - but it is calculated rather than chosen, so the tab shows it once as a
 * read-only field, mirroring the single-bond ticket's "Settlement Date" box.
 */
const LIST_RFQ_BASE_COLUMNS = [
  { key: "cusip", label: "Identifier", width: 176, tag: "48 SecurityID" },
  { key: "side", label: "Side", width: 78, tag: "54 Side" },
  // 124px, not 100: the ceiling is 10,000,000 and the input carries thousand separators,
  // so the widest legal value is "10,000,000" - which was clipping at 100.
  { key: "qty", label: "Quantity", width: 124, tag: "38 OrderQty" },
  // 104px, not 88: the values are 15px now, and a price carries up to six decimals
  // (LIST_RFQ_PRICE_MAX is 150), so "150.123456" needs more room than the old width gave it.
  { key: "price", label: "Price", width: 104, tag: "" },
  // 128px, not 104: SPREAD is the widest value this select offers, and even with the
  // paddingRight fix for the dropdown arrow (listRfqStyles.compactInputStyle) it read as
  // cramped at 104 - widened per direct browser feedback.
  { key: "priceType", label: "Price Type", width: 128, tag: "423 PriceType" },
];

/**
 * Columns 234/28007/453 - Trumid only. Stipulation and partial-fill have no MarketAxess
 * equivalent on FixNewOrderList.Line at all. Dealers (453/9690 at Trumid's list level) is
 * different: MarketAxess's OWN list-level dealer targeting exists too (listRfqData.mktxVenue,
 * shown once above the grid, not per row), because the backend requires it identical across
 * every bond (NewOrderListService.requireSameListLevelValue) - this per-row picker would let a
 * trader pick disagreeing dealers per bond and have the whole list refused, so MarketAxess gets
 * its own, different, per-row column instead - see orderDealers below.
 */
const TRUMID_ONLY_COLUMNS = [
  { key: "stipulationType", label: "Stipulation", width: 104, tag: "234 StipulationValue" },
  { key: "allowPartialExecution", label: "Partial", width: 84, tag: "28007 AllowPartialFills" },
  { key: "venue", label: "Dealers", width: 190, tag: "453 NoPartyIDs" },
];

/**
 * MarketAxess only, genuinely per bond (see createEmptyRow's comments on each field - none of
 * the six requires cross-bond agreement, unlike venue/mktxVenue above).
 *
 * >20130's own child tags support an include/exclude choice per dealer (>>20133); "Item
 * Dealers" below does not expose it, deliberately mirroring the single-bond ticket's own Dealer
 * field, which has no exclude concept either - see createEmptyRow's orderDealers comment for
 * the full reasoning.
 *
 * Target Level (>5214) is the one column with no cross-field validation at all - just an
 * optional number, same as the single-bond ticket's own "Target Level" field.
 *
 * Strategy Params (>957) is the one column that does not fit a fixed-width cell at all - the
 * definition set is dynamic per strategy type (fetched from the server, anywhere from zero to
 * several fields, each its own input type) - so unlike every other MarketAxess column it opens
 * a modal instead of rendering inline, the same "one shared dialog, opened per row" shape
 * ListRfqTab already uses for Bond Details. See createEmptyRow's strategyParameters comment.
 *
 * Effective/Expiry Time were originally built list-level (one pair for the whole list), then
 * moved here per-row on review (2026-09-28): the backend places no cross-bond constraint on
 * either, and per-row is consistent with Strategy/Item Dealers above, which are per-row for the
 * same reason.
 */
const MARKETAXESS_ONLY_COLUMNS = [
  // 128px, not 104 - PROVIDE is the widest value this select offers; same reasoning and same
  // width as Price Type above, widened per the same direct browser feedback.
  { key: "strategyType", label: "Strategy", width: 128, tag: "847 TargetStrategy" },
  { key: "orderDealers", label: "Item Dealers", width: 190, tag: "20130 NoMKTXOrderDealers" },
  // Same width as Price above and the same reason: six decimals (FloatInput's decimalScale,
  // matching the single-bond ticket's own Target Level field) needs more room than a plain
  // price-style field.
  { key: "mktxTargetLevel", label: "Target Level", width: 104, tag: "5214 mktxTargetLevel" },
  // Leave room for the full date, time, AM/PM and calendar button in the compact picker.
  { key: "effectiveTime", label: "Effective Time", width: 230, tag: "168 EffectiveTime" },
  { key: "expiryTime", label: "Expiry Time", width: 230, tag: "126 ExpireTime" },
  // Last on purpose, not grouped next to Strategy: it opens a dialog rather than holding a
  // value inline (see the class doc above), so it reads as a secondary/optional action on the
  // row rather than one of the row's own data fields.
  { key: "strategyParameters", label: "Strategy Params", width: 180, tag: "957 StrategyParametersGrp" },
];

/**
 * One icon: remove. Was 72 while it also held bond details, which now lives in the identifier
 * field's endAdornment - a single 18px icon in 4px padding needs ~26px plus the cell's gutter.
 * grow:false - it holds an icon button, so spare width here would just be a gap.
 */
const ACTIONS_COLUMN = { key: "actions", label: "", width: 44, tag: "", grow: false };

/**
 * Where each bond of an existing list actually got to - OPEN, TRADED, CANCELED and so on.
 *
 * Shown ONLY when the grid is read-only, i.e. a list being looked at rather than built. A bond
 * the trader is still typing has no venue status, so the column would be empty on every row.
 *
 * It takes the place of the remove button rather than sitting beside it: nothing on a read-only
 * grid can be removed, so that column is 44px of disabled icon. Swapping keeps the grid close
 * to the width it already had instead of pushing it further off the side of an 800px modal.
 */
const STATUS_COLUMN = { key: "rfqStatus", label: "Status", width: 116, tag: "" };

/**
 * The columns, which differ in two places: the last column swaps (see STATUS_COLUMN), and the
 * Trumid-only/MarketAxess-only trio in the middle swaps with the chosen protocol (see
 * TRUMID_ONLY_COLUMNS/MARKETAXESS_ONLY_COLUMNS).
 *
 * A function rather than a constant for both reasons. Both the header in ListRfqTab and the
 * cells here call it with the same two arguments, so they cannot disagree about how many
 * columns there are, what each one is, or how wide it is.
 */
export const listRfqColumns = (readOnly = false, protocol = PROTOCOL_TYPES.TRUMID_RFQ, dealerScope = 'LIST') => [
  ...LIST_RFQ_BASE_COLUMNS,
  ...(protocol === PROTOCOL_TYPES.MARKETAXESS
    ? MARKETAXESS_ONLY_COLUMNS.filter((column) => column.key !== 'orderDealers' || ['ITEM', 'BOTH'].includes(dealerScope))
    : TRUMID_ONLY_COLUMNS),
  readOnly ? STATUS_COLUMN : ACTIONS_COLUMN,
];

export const listRfqRowMinWidth = (readOnly = false, protocol = PROTOCOL_TYPES.TRUMID_RFQ, dealerScope = 'LIST') =>
  listRfqColumns(readOnly, protocol, dealerScope).reduce((sum, c) => sum + c.width, 0);

/**
 * How one column sizes itself - used by BOTH the header cell and the row cell.
 *
 * In plain terms: the columns stretch to fill the grid instead of leaving a gap down the
 * right-hand side, and they stretch in proportion so the layout keeps its shape.
 *
 * flexBasis is the designed width and flexGrow is that same number, so spare width is shared
 * in proportion to how wide each column already is - a 190px Dealers column takes roughly
 * twice what a 88px Price column takes, rather than every column gaining the same amount.
 * flexShrink 0 keeps the designed width as a floor, so below LIST_RFQ_ROW_MIN_WIDTH the grid
 * scrolls sideways exactly as it did rather than squashing the inputs.
 *
 * Exported, and used in both places, because the header in ListRfqTab draws these same
 * columns: two copies of this arithmetic would let the headings drift out of line with the
 * cells underneath them.
 */
export const listRfqColumnSx = (column) => ({
  flexGrow: column.grow === false ? 0 : column.width,
  flexShrink: 0,
  flexBasis: column.width,
  px: 0.375,
});

const ALL_DEALER_OPTION = "ALL";

/**
 * Typing-time ceilings on quantity and price.
 *
 * Both copied from the single-bond ticket so a bond entered on a list cannot carry a value
 * the same bond entered on its own would have refused - ActualOrderTabOrderPrimaryFields
 * passes maxValue={10000000} to the quantity input (~line 288) and maxValue={150} to the
 * price input (~line 302). Named here rather than inlined so the two screens can be diffed.
 *
 * These stop the value being typed at all. They sit on top of, not instead of, the
 * submit-time checks in validateListRfq (quantity above zero and at or above the security's
 * minimum, price above zero).
 */
const QTY_MAX = 10000000;
const PRICE_MAX = 150;

/**
 * How the selected dealers read inside the closed field.
 *
 * Copied from formatVenues in ActualOrderTab (line ~1071) so the list picker shows exactly
 * what the single-bond picker shows, down to the 25-character cut-off.
 */
const formatVenues = (venues) => {
  if (!venues) return "";
  if (typeof venues === "string") return venues;
  const venueString = venues.join(", ");
  return venueString.length > 25 ? venueString.substring(0, 22) + "..." : venueString;
};

/** Copied from MarketAxessFields.jsx's parseUtcMs (see createEmptyRow's effectiveTime/
 *  expiryTime comment for why this feature's helpers are duplicated rather than shared). */
const parseRowUtcMs = (val) => {
  if (!val) return NaN;
  try {
    const str = String(val).trim();
    const withZ = /[zZ]|[+-]\d{2}:\d{2}$/.test(str) ? str : `${str}Z`;
    const d = new Date(withZ);
    return isNaN(d.getTime()) ? NaN : d.getTime();
  } catch {
    return NaN;
  }
};

/**
 * One bond of a List RFQ - see listRfqColumns for what each field maps to.
 *
 * HOW THIS ROW REPORTS A PROBLEM: it turns the offending cell red and says nothing.
 *
 * `errors` is a map of `{ [columnKey]: true }` built by validateListRfq, keyed by the very
 * keys in listRfqColumns, and each cell passes its own entry to MUI as `error`. The red
 * border comes from the shared `inputStyle` painting `.Mui-error` (styles.js ~line 85), which
 * compactInputStyle inherits - so it is the same red, from the same place, as every field on
 * the Bond Order tab, which reports its problems the same way and equally without words
 * (ActualOrderTabOrderPrimaryFields passes `errors?.<field>` to each input and carries no
 * helperText).
 *
 * The row itself is not tinted and carries no error line beneath it. Both were this grid's
 * own invention; the single-bond ticket has neither, and a row that is red all over cannot
 * point at the cell that is actually wrong.
 */
const ListRfqBondRow = ({
  row,
  errors = {},
  dealerChoices,
  readOnly,
  onChange,
  onValidateCusip,
  onRemove,
  onShowDetails,
  onOpenStrategyParams,
  isDuplicate = false,
  protocol = PROTOCOL_TYPES.TRUMID_RFQ,
  dealerScope = 'LIST',
}) => {
  useEffect(() => {
    if (readOnly || !(row.cusip || '').trim()) return;
    const timer = setTimeout(() => onValidateCusip(row.rowId), 500);
    return () => clearTimeout(timer);
  }, [row.cusip, row.rowId, readOnly, protocol, onValidateCusip]);

  const isMarketAxess = protocol === PROTOCOL_TYPES.MARKETAXESS;
  const isSpread = row.priceType === TRUMID_PRICE_TYPES.SPREAD;

  // Trimmed and upper-cased the same way buildListRfqPayload sends tag 48, so the bond the
  // details dialog shows is the bond the request would carry.
  const identifier = (row.cusip || "").trim().toUpperCase();

  const [dealerDropdownOpen, setDealerDropdownOpen] = useState(false);

  // Mirrors the single-bond picker: ALL collapses the display to just ALL, and any value
  // already on the order that the dealer API no longer returns is kept visible.
  const rawVenues = Array.isArray(row.venue) ? row.venue : [];
  const selectedVenues = rawVenues.includes(ALL_DEALER_OPTION)
    ? [ALL_DEALER_OPTION]
    : rawVenues;
  const optionsForThisRow = [
    ...dealerChoices,
    ...selectedVenues.filter((v) => !dealerChoices.includes(v)),
  ];
  const dealerPreviewValue = formatVenues(selectedVenues);

  // >957 Strategy Params (MarketAxess only) - count of values actually entered, for the
  // button label below. Excludes empty-string entries so a param the trader opened and then
  // cleared back out does not count as "set".
  const strategyParamCount = Object.values(row.strategyParameters || {}).filter(
    (v) => v !== "" && v !== null && v !== undefined
  ).length;

  // >20130 Item Dealers (MarketAxess only) - same ALL sentinel and preview as venue above. This picker supplements or replaces the list-wide dealer picker when per-bond
  // targeting is selected.
  const [orderDealersDropdownOpen, setOrderDealersDropdownOpen] = useState(false);
  const selectedOrderDealers = Array.isArray(row.orderDealers) ? row.orderDealers : [];
  const orderDealerChoices = [ALL_DEALER_OPTION, ...dealerChoices.filter((o) => o !== ALL_DEALER_OPTION)];
  const orderDealerOptionsForThisRow = [
    ...orderDealerChoices,
    ...selectedOrderDealers.filter((v) => !orderDealerChoices.includes(v)),
  ];
  const orderDealerPreviewValue = formatVenues(selectedOrderDealers);

  // >168/>126 Effective/Expiry Time (MarketAxess only) bounds - copied from
  // MarketAxessFields.jsx's own futureMin/localExpiryForBounds/expiryMin/hasTimeError (see
  // createEmptyRow's effectiveTime/expiryTime comment for why this whole feature is copied
  // rather than imported). Computed per row, from that row's own effectiveTime/expiryTime -
  // unlike the single-bond ticket, where there is only one bond's worth of these to bound.
  const nowLocal = dayjs();
  const localEffective = utcToLocal(row.effectiveTime);
  const localExpiry = utcToLocal(row.expiryTime);
  const expiryMin = localEffective && localEffective.isAfter(nowLocal) ? localEffective : nowLocal;
  const effMs = parseRowUtcMs(row.effectiveTime);
  const expMs = parseRowUtcMs(row.expiryTime);
  const hasTimeError = Number.isFinite(effMs) && Number.isFinite(expMs) && effMs > expMs;

  // Sized from the same column set the header drew, so a read-only row lines up under the
  // read-only header rather than under the editable one's narrower last column.
  const columns = listRfqColumns(readOnly, protocol, dealerScope);
  const cell = (key) => listRfqColumnSx(columns.find((c) => c.key === key));

  const statusIcon = () => {
    if (row.status === ROW_STATUS.CHECKING) return <CircularProgress size={13} sx={{ mr: 0.5 }} />;
    // A found bond shows the details button rather than a tick. The button's presence IS the
    // "CUSIP found" signal the tick used to give - it only ever appears in this branch - so the
    // row still confirms the lookup succeeded without spending a second icon on saying so. It
    // keeps tokens.palette.primary for that reason: same colour, same position, same meaning,
    // now also clickable. Moved here from the actions column so the control sits on the value it
    // describes instead of at the far end of the row.
    //
    // No disabled state and no hasIdentifier guard: this branch is only reached once the lookup
    // has come back VALID, which cannot happen without an identifier. Invalid and in-flight rows
    // keep their own icons below and offer no details, because there is no bond to show.
    if (row.status === ROW_STATUS.VALID) {
      return (
        <Tooltip title="Bond details">
          <IconButton
            size="small"
            onClick={() => onShowDetails(identifier)}
            aria-label={`Bond details for ${identifier}`}
            // Smaller and tighter than the 18px/p:0.5 it had in the actions column: it now sits
            // inside the input, where compactInputStyle gives a 34px row to fit into.
            sx={{ p: 0.25, mr: -0.25, color: tokens.palette.primary }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      );
    }
    if (row.status === ROW_STATUS.INVALID) {
      return (
        <Tooltip title={row.message || "Invalid CUSIP"}>
          <ErrorOutlineIcon sx={{ fontSize: 15, mr: 0.5, color: tokens.palette.danger }} />
        </Tooltip>
      );
    }
    return null;
  };

  /**
   * ALL is mutually exclusive with naming individual dealers - the same rule as the
   * single-bond picker (handleDealerAutocompleteChange in
   * ActualOrderTabOrderPrimaryFields). Named dealers collapse to "ALL" only at submit, in
   * resolveVenueForSubmit.
   */
  const handleDealers = (_event, values, reason, details) => {
    const selection = Array.from(
      new Set((values || []).map((v) => String(v).trim()).filter(Boolean))
    );
    const hasAll = selection.includes(ALL_DEALER_OPTION);
    const dealers = selection.filter((v) => v !== ALL_DEALER_OPTION);

    let next;
    if (reason === "clear") next = [];
    else if (details?.option === ALL_DEALER_OPTION) next = hasAll ? [ALL_DEALER_OPTION] : [];
    else next = dealers;

    onChange(row.rowId, { venue: next });
  };

  // Same ALL exclusivity as the single and list-wide dealer pickers.
  const handleOrderDealers = (_event, values, reason, details) => {
    onChange(row.rowId, { orderDealers: selectMktxDealers(values, reason, details) });
  };

  return (
    <Box
      sx={{
        // Same rule colour the Market Data table's cells get from MuiTableCell (theme.js
        // ~line 522), rather than a locally-computed 40% of the full-strength colour.
        borderBottom: `1px solid ${tokens.colors.defaultGridBorderColor}`,
        // White, so the rows sit against the grey header band exactly as MuiTableRow's white
        // does under Market Data's head. Transparent would let the container show through and
        // leave header and rows the same shade.
        //
        // Always white, including when the row has errors: a bad row is shown by its bad cell
        // going red, not by the whole row changing colour. The Bond Order tab tints nothing.
        backgroundColor: tokens.colors.white,
        minWidth: listRfqRowMinWidth(readOnly, protocol, dealerScope),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", px: 0.75, py: 0.5 }}>
        <Box sx={cell("cusip")}>
          <TextField
            fullWidth
            // name="cusip" matches the single-bond ticket's Identifier field
            // (ActualOrderTabOrderPrimaryFields ~line 220). Neither screen supplies its own
            // suggestion list - the dropdown a trader sees on either is the browser's own
            // saved form history, which it buckets by field name. Without a name here the
            // browser fell back to guessing and offered values saved from unrelated fields.
            // Sharing the name puts this field in the same bucket as the single ticket, so
            // it offers the same previously-typed identifiers.
            name="cusip"
            placeholder="CUSIP / ISIN"
            value={row.cusip}
            disabled={readOnly}
            onChange={(e) => onChange(row.rowId, { cusip: e.target.value.toUpperCase() })}
            variant="filled"
            // Red border on both offending cells the moment the same bond is typed twice,
            // with no message: two identical identifiers sitting next to each other say it
            // themselves. Send is disabled for the same reason at the same moment
            // (OrderEntryCore), both reading the one rule in listRfqPayload.findDuplicateCusips.
            //
            // The border needs no styling of its own - inputStyle already paints .Mui-error
            // as a danger-coloured border (styles.js ~line 85), and compactInputStyle
            // inherits it, so this is the same red every other field in the modal uses.
            // Red as soon as the lookup comes back bad, rather than waiting for submit.
            // ROW_STATUS.INVALID covers an unknown identifier, a failed lookup, and a
            // non-corporate bond; the reason itself is on the hover of the icon to the
            // right of this field (statusIcon), so the cell stays narrow.
            error={!!errors.cusip || isDuplicate || row.status === ROW_STATUS.INVALID}
            sx={compactInputStyle}
            InputProps={{ disableUnderline: true, endAdornment: statusIcon() }}
          />
        </Box>

        <Box sx={cell("side")}>
          <TextField
            fullWidth
            select
            value={row.side || ORDER_SIDES.BUY}
            disabled={readOnly}
            onChange={(e) => onChange(row.rowId, { side: e.target.value })}
            variant="filled"
            error={!!errors.side}
            sx={compactInputStyle}
            InputProps={{ disableUnderline: true }}
          >
            {fieldOptions.side.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Quantity and Price use the very same shared inputs as the single-bond ticket
            (NumericInputs.jsx, as wired in ActualOrderTabOrderPrimaryFields ~lines 262 and
            294), rather than a NumericFormat configured by hand here. That is deliberate:
            an earlier hand-rolled version of these two cells silently lost the typing-time
            limits the single ticket enforces - no quantity ceiling, no price ceiling, six
            decimals instead of four, and no thousand separators on price. Reusing the
            components means those limits cannot drift apart again. */}
        <Box sx={cell("qty")}>
          <IntegerInput
            name="qty"
            value={row.qty}
            disabled={readOnly}
            placeholder={isMarketAxess ? "In 1000s" : row.minQty != null ? `min ${row.minQty}` : "Qty"}
            maxValue={QTY_MAX}
            step={isMarketAxess ? 1000 : 1}
            // Covers both a missing quantity and one below this bond's minimum - the same
            // cell either way, which is why validateListRfq chains those two checks.
            error={!!errors.qty}
            sx={compactInputStyle}
            InputProps={{ disableUnderline: true }}
            onChange={(e) => onChange(row.rowId, { qty: e.target.value })}
          />
        </Box>

        <Box sx={cell("price")}>
          <FloatInput
            name="price"
            value={row.price}
            disabled={readOnly}
            placeholder="Price"
            maxValue={PRICE_MAX}
            error={!!errors.price}
            sx={compactInputStyle}
            InputProps={{ disableUnderline: true }}
            onChange={(e) => onChange(row.rowId, { price: e.target.value })}
          />
        </Box>

        <Box sx={cell("priceType")}>
          <TextField
            fullWidth
            select
            value={row.priceType || ""}
            disabled={readOnly}
            onChange={(e) => onChange(row.rowId, { priceType: e.target.value })}
            variant="filled"
            error={!!errors.priceType}
            sx={compactInputStyle}
            InputProps={{ disableUnderline: true }}
          >
            {(isMarketAxess ? fieldOptions.mktxPriceType : [
              TRUMID_PRICE_TYPES.PRICE,
              TRUMID_PRICE_TYPES.SPREAD,
            ]).map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        </Box>

        {isMarketAxess ? (
          <>
          <Box sx={cell("strategyType")}>
            <TextField
              fullWidth
              select
              value={row.strategyType || MKTX_STRATEGY_TYPES.TAKE}
              disabled={readOnly}
              onChange={(e) =>
                // Strategy Params below are only ever valid for the strategy type they were
                // entered under (the fetched definitions differ by type) - cleared here on
                // every genuine change, the same "isTypeChanged -> reset" rule
                // ActualOrderTab.loadStrategyParams applies, so a value can never silently
                // carry over onto a different, unrelated set of parameters.
                onChange(row.rowId, {
                  strategyType: e.target.value,
                  strategyParameters: {},
                  ...(!mktxStrategyUsesDealers(e.target.value) ? { orderDealers: [] } : {}),
                })
              }
              variant="filled"
              error={!!errors.strategyType}
              sx={compactInputStyle}
              InputProps={{ disableUnderline: true }}
            >
              {fieldOptions.strategyType.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
          </Box>

          {['ITEM', 'BOTH'].includes(dealerScope) && <Box sx={cell("orderDealers")}>
            {/* Keep the picker visible in mixed lists; SEEK/PROVIDE cannot target dealers. */}
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              openOnFocus
              options={orderDealerOptionsForThisRow}
              value={selectedOrderDealers}
              onChange={handleOrderDealers}
              onOpen={() => setOrderDealersDropdownOpen(true)}
              onClose={() => setOrderDealersDropdownOpen(false)}
              disabled={readOnly || !mktxStrategyUsesDealers(row.strategyType)}
              isOptionEqualToValue={(option, value) => option === value}
              getOptionLabel={(option) => option || ""}
              renderOption={(props, option, { selected }) => (
                <Box component="li" {...props} sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={selected}
                    sx={{ width: 16, height: 16, p: 0, mr: 1, borderRadius: 0 }}
                    disableRipple
                  />
                  <Typography sx={{ fontSize: 13, fontWeight: selected ? 700 : 400 }}>
                    {option}
                  </Typography>
                </Box>
              )}
              renderTags={() => null}
              renderInput={(params) => (
                <Box sx={{ position: "relative" }}>
                  <TextField
                    {...params}
                    fullWidth
                    variant="filled"
                    error={!!errors.orderDealers}
                    sx={dealerInputStyle}
                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                  />
                  {!orderDealersDropdownOpen && !!orderDealerPreviewValue && (
                    <Typography
                      sx={{
                        position: "absolute",
                        left: "9px",
                        right: `${DEALER_PREVIEW_RIGHT_PX}px`,
                        top: "50%",
                        transform: "translateY(-50%)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        pointerEvents: "none",
                        fontSize: "15px",
                        color: readOnly || !mktxStrategyUsesDealers(row.strategyType)
                          ? tokens.colors.disabledFontColour : tokens.colors.fontColour,
                      }}
                    >
                      {orderDealerPreviewValue}
                    </Typography>
                  )}
                </Box>
              )}
            />
          </Box>}

          <Box sx={cell("mktxTargetLevel")}>
            {/* >5214 - the same FloatInput/decimalScale=6 combination the single-bond
                ticket's own Target Level field uses (MarketAxessFields.jsx); optional, so no
                error prop wired - there is no invalid value short of a non-number, which
                FloatInput already refuses to accept as input. */}
            <FloatInput
              name="mktxTargetLevel"
              value={row.mktxTargetLevel}
              disabled={readOnly}
              placeholder="Target Level"
              decimalScale={6}
              sx={compactInputStyle}
              InputProps={{ disableUnderline: true }}
              onChange={(e) => onChange(row.rowId, { mktxTargetLevel: e.target.value })}
            />
          </Box>

          <Box sx={cell("effectiveTime")}>
            {/* No label, matching every other cell in this row - the column header
                (listRfqColumns) is the label. Bounds/error logic copied from
                MarketAxessFields.jsx, see the nowLocal/localEffective block above. */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                value={localEffective}
                onChange={(v) => onChange(row.rowId, { effectiveTime: localToUtc(v) })}
                minDateTime={nowLocal}
                maxDateTime={localExpiry ?? undefined}
                closeOnSelect={false}
                disabled={readOnly}
                slotProps={{
                  textField: {
                    variant: "filled",
                    fullWidth: true,
                    error: !!errors.effectiveTime || hasTimeError,
                    sx: {
                      ...compactInputStyle,
                      '& .MuiInputAdornment-root': { marginLeft: '-8px' },
                    },
                    InputProps: { disableUnderline: true },
                  },
                  popper: { placement: "auto" },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={cell("expiryTime")}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                value={localExpiry}
                onChange={(v) => onChange(row.rowId, { expiryTime: localToUtc(v) })}
                minDateTime={expiryMin}
                closeOnSelect={false}
                disabled={readOnly}
                slotProps={{
                  textField: {
                    variant: "filled",
                    fullWidth: true,
                    error: !!errors.expiryTime || hasTimeError,
                    sx: {
                      ...compactInputStyle,
                      '& .MuiInputAdornment-root': { marginLeft: '-8px' },
                    },
                    InputProps: { disableUnderline: true },
                  },
                  popper: { placement: "auto" },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={cell("strategyParameters")}>
            {/* >957 - the one MarketAxess field with no inline widget: definitions are
                fetched per strategy type and vary in count/shape, so they cannot live in a
                fixed-width cell. Opens the shared modal ListRfqTab mounts once for the whole
                grid, the same "one dialog, not one per row" shape Bond Details already uses. */}
            <Button
              size="small"
              variant="text"
              onClick={() => onOpenStrategyParams(row.rowId)}
              startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
              sx={{
                ...compactInputStyle,
                ...(errors.strategyParameters ? { border: `1px solid ${tokens.colors.dangerColor || '#d32f2f'}` } : {}),
                justifyContent: "flex-start",
                textTransform: "none",
                fontSize: "15px",
                color: tokens.palette.primary,
                textDecoration: "underline",
                fontWeight: 400,
                "&:hover": { textDecoration: "underline" },
                minWidth: 0,
              }}
            >
              {strategyParamCount > 0 ? `${strategyParamCount} Configured` : "Configure"}
            </Button>
          </Box>
          </>
        ) : (
          <>
        <Box sx={cell("stipulationType")}>
          {/* Conditionally required when PriceType=6 (spec p.50), meaningless otherwise. */}
          <TextField
            fullWidth
            select
            value={isSpread ? row.stipulationType || "" : ""}
            disabled={readOnly || !isSpread}
            onChange={(e) => onChange(row.rowId, { stipulationType: e.target.value })}
            variant="filled"
            // Only ever set on a SPREAD bond, which is the only time this cell is enabled -
            // so this never asks a disabled field to show an error it cannot be fixed in.
            error={!!errors.stipulationType}
            sx={compactInputStyle}
            InputProps={{ disableUnderline: true }}
          >
            <MenuItem value={TRUMID_STIPULATION_TYPES.HEDGE}>HEDGE</MenuItem>
            <MenuItem value={TRUMID_STIPULATION_TYPES.SPOT}>SPOT</MenuItem>
          </TextField>
        </Box>

        <Box sx={cell("allowPartialExecution")}>
          <TextField
            fullWidth
            select
            value={row.allowPartialExecution || TRUMID_PARTIAL_EXECUTION.YES}
            disabled={readOnly}
            onChange={(e) => onChange(row.rowId, { allowPartialExecution: e.target.value })}
            variant="filled"
            error={!!errors.allowPartialExecution}
            sx={compactInputStyle}
            InputProps={{ disableUnderline: true }}
          >
            <MenuItem value={TRUMID_PARTIAL_EXECUTION.YES}>Yes</MenuItem>
            <MenuItem value={TRUMID_PARTIAL_EXECUTION.NO}>No</MenuItem>
          </TextField>
        </Box>

        <Box sx={cell("venue")}>
          {/* Behaves exactly as the single-bond Dealer field does
              (ActualOrderTabOrderPrimaryFields): multi-select with a checkbox per option,
              ALL pinned first and mutually exclusive, chips suppressed via
              renderTags={() => null}, and the selection shown instead as a single truncated
              line that hides while the dropdown is open. Selected values that are no longer
              in the API's dealer list are appended so they stay visible, same as there. */}
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            openOnFocus
            options={optionsForThisRow}
            value={selectedVenues}
            onChange={handleDealers}
            onOpen={() => setDealerDropdownOpen(true)}
            onClose={() => setDealerDropdownOpen(false)}
            disabled={readOnly}
            isOptionEqualToValue={(option, value) => option === value}
            getOptionLabel={(option) => option || ""}
            renderOption={(props, option, { selected }) => (
              <Box component="li" {...props} sx={{ display: "flex", alignItems: "center" }}>
                {option !== ALL_DEALER_OPTION && (
                  <Checkbox
                    checked={selected}
                    sx={{ width: 16, height: 16, p: 0, mr: 1, borderRadius: 0 }}
                    disableRipple
                  />
                )}
                <Typography
                  sx={{ fontSize: 13, fontWeight: selected ? 700 : 400 }}
                >
                  {option}
                </Typography>
              </Box>
            )}
            renderTags={() => null}
            renderInput={(params) => (
              <Box sx={{ position: "relative" }}>
                <TextField
                  {...params}
                  fullWidth
                  variant="filled"
                  error={!!errors.venue}
                  sx={dealerInputStyle}
                  InputProps={{ ...params.InputProps, disableUnderline: true }}
                />
                {!dealerDropdownOpen && !!dealerPreviewValue && (
                  <Typography
                    sx={{
                      position: "absolute",
                      left: "9px",
                      // Clears BOTH icon buttons, not just the arrow - see
                      // DEALER_PREVIEW_RIGHT_PX for why the old value looked right until
                      // the trader hovered over the cell.
                      right: `${DEALER_PREVIEW_RIGHT_PX}px`,
                      top: "50%",
                      transform: "translateY(-50%)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                      // 15px, the same as every other cell in the row - compactInputStyle
                      // sets that on .MuiFilledInput-input. This read 13px, so the dealer
                      // names were visibly smaller than the quantity and price beside them,
                      // and smaller than this same box's own text once the dropdown opened.
                      fontSize: "15px",
                      color: readOnly
                        ? tokens.colors.disabledFontColour
                        : tokens.colors.fontColour,
                    }}
                  >
                    {dealerPreviewValue}
                  </Typography>
                )}
              </Box>
            )}
          />
        </Box>
          </>
        )}

        {/* Read-only: where this bond actually got to. Plain text rather than a disabled
            input, because it is not a field the trader ever sets - it is what came back from
            the venue. "---" when the list predates a status or the bond never reached one, so
            the column never reads as an empty box. */}
        {readOnly ? (
          <Box sx={{ ...cell("rfqStatus"), whiteSpace: "nowrap", overflow: "hidden" }}>
            <Typography
              title={row.rfqStatus || ""}
              sx={{
                fontSize: 13,
                color: tokens.colors.fontColour,
                overflow: "hidden",
                textOverflow: "ellipsis",
                px: 0.5,
              }}
            >
              {row.rfqStatus || "---"}
            </Typography>
          </Box>
        ) : (
        <Box sx={{ ...cell("actions"), textAlign: "center", whiteSpace: "nowrap" }}>
          {/* Bond details used to sit here. It is now the endAdornment of the identifier field
              (statusIcon), on the value it describes. This column holds only remove. */}

          {/* Red, because removing a row is the one destructive thing on this grid - the same
              tokens.palette.danger the invalid-identifier icon uses, so there is one red in
              this component rather than a second one invented here. Faded rather than grey
              when disabled, so a read-only row still reads as the same control. */}
          <IconButton
            size="small"
            disabled={readOnly}
            onClick={() => onRemove(row.rowId)}
            aria-label={`Remove bond ${row.cusip || ""}`}
            sx={{
              p: 0.5,
              color: tokens.palette.danger,
              "&.Mui-disabled": { color: withAlpha(tokens.palette.danger, 0.35) },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        )}
      </Box>
    </Box>
  );
};

export default ListRfqBondRow;
